import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, DollarSign, User, MapPin, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, RawMaterial } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';

interface EditRawMaterialModalProps {
  material: RawMaterial;
  onClose: () => void;
  onSuccess: () => void;
}

const EditRawMaterialModal: React.FC<EditRawMaterialModalProps> = ({ material, onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const { getCategoryNames } = useCategories('rawMaterial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: material.name || '',
    category: material.category || '',
    minStock: material.minStock || 0,
    maxStock: material.maxStock || 0,
    mainUnit: material.mainUnit || '',
    subUnit: material.subUnit || '',
    subUnitsPerMainUnit: material.subUnitsPerMainUnit || 1,
    costPerMainUnit: material.costPerMainUnit || 0,
    supplier: material.supplier || '',
    supplierContact: material.supplierContact || '',
    location: material.location || '',
    description: material.description || '',
    expiryDate: (() => {
      if (!material.expiryDate) return '';
      try {
        const date = new Date(material.expiryDate);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch (error) {
        console.warn('Invalid expiry date:', material.expiryDate);
        return '';
      }
    })()
  });

  const categories = getCategoryNames();

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Please enter material name');
      return;
    }

    if (!formData.category) {
      setError('Please select a category');
      return;
    }

    if (formData.minStock < 0 || formData.maxStock < 0) {
      setError('Stock levels cannot be negative');
      return;
    }

    if (formData.maxStock <= formData.minStock) {
      setError('Maximum stock must be greater than minimum stock');
      return;
    }

    if (formData.subUnitsPerMainUnit <= 0) {
      setError('Sub-units per main unit must be greater than 0');
      return;
    }

    if (formData.costPerMainUnit < 0) {
      setError('Cost cannot be negative');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const costPerSubUnit = formData.costPerMainUnit / formData.subUnitsPerMainUnit;

      const updateData: Partial<RawMaterial> = {
        name: formData.name.trim(),
        category: formData.category,
        minStock: formData.minStock,
        maxStock: formData.maxStock,
        mainUnit: formData.mainUnit.trim(),
        subUnit: formData.subUnit.trim(),
        subUnitsPerMainUnit: formData.subUnitsPerMainUnit,
        costPerMainUnit: formData.costPerMainUnit,
        costPerSubUnit,
        supplier: formData.supplier.trim(),
        supplierContact: formData.supplierContact.trim() || undefined,
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : undefined,
        
        // Update legacy fields for backward compatibility
        baseUnit: formData.subUnit.trim(),
        costPerUnit: costPerSubUnit,
        unitConversions: [
          {
            unitName: formData.mainUnit.trim(),
            conversionFactor: formData.subUnitsPerMainUnit,
            isPurchaseUnit: true,
            isSaleUnit: true,
            costPerConversionUnit: formData.costPerMainUnit
          }
        ],
        defaultPurchaseUnit: formData.mainUnit.trim(),
        defaultSaleUnit: formData.subUnit.trim()
      };

      await dataService.update<RawMaterial>(tenantData!.id, 'rawMaterials', material.id!, updateData);
      onSuccess();
    } catch (error) {
      console.error('Error updating raw material:', error);
      setError('Failed to update raw material. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl tablet:text-2xl font-bold text-gray-900">
            Edit Raw Material
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Package className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="Enter material name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="input-field"
                  placeholder="Optional description"
                />
              </div>
            </div>

            {/* Stock & Units */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Stock & Units</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Stock *
                  </label>
                  <input
                    type="number"
                    name="minStock"
                    required
                    min="0"
                    value={formData.minStock}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Stock *
                  </label>
                  <input
                    type="number"
                    name="maxStock"
                    required
                    min="1"
                    value={formData.maxStock}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Unit (Purchase) *
                  </label>
                  <input
                    type="text"
                    name="mainUnit"
                    required
                    value={formData.mainUnit}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="e.g., box, carton"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub Unit (Sale) *
                  </label>
                  <input
                    type="text"
                    name="subUnit"
                    required
                    value={formData.subUnit}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="e.g., piece, bottle"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-units per Main Unit *
                </label>
                <input
                  type="number"
                  name="subUnitsPerMainUnit"
                  required
                  min="1"
                  value={formData.subUnitsPerMainUnit}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="e.g., 20 pieces per box"
                />
              </div>
            </div>
          </div>

          {/* Cost & Supplier Information */}
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6 mt-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Cost Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cost per {formData.mainUnit || 'Main Unit'} *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    name="costPerMainUnit"
                    required
                    min="0"
                    step="0.01"
                    value={formData.costPerMainUnit}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0.00"
                  />
                </div>
                {formData.costPerMainUnit > 0 && formData.subUnitsPerMainUnit > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    = ₹{(formData.costPerMainUnit / formData.subUnitsPerMainUnit).toFixed(4)} per {formData.subUnit || 'sub-unit'}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Supplier Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="supplier"
                    required
                    value={formData.supplier}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="Supplier name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Supplier Contact
                </label>
                <input
                  type="text"
                  name="supplierContact"
                  value={formData.supplierContact}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Phone or email"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Storage Location
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="Storage location"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default EditRawMaterialModal; 